/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { useProductSkuDetails } from '@/features/product/api/getProductSkuDetails';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';

/**
 * Determines if a product has any specifications to display on the product detail page.
 *
 * Checks for the existence of various product specification fields including:
 * - Warranty information
 * - Item weight
 * - Location
 * - Application notes (Parts Pro only, distinct from techNotes/quickNotes/displayNotes)
 * - Product attributes (excluding Part Number, SKU Number, Cross Reference Number — hidden per SEB-15628)
 * - Product service bulletins (PSB) and MSDS documents
 *
 * @returns {boolean} True if any product specifications exist, false otherwise
 */
export const useProductSpecificationsExist = (): boolean => {
  const HIDDEN_SPEC_KEYS = new Set(['Part Number', 'SKU Number', 'Cross Reference Number']);

  const { data: productDetailsData, status: productDetailsStatus } = useProductDetails();
  const product = productDetailsData?.product;

  const skuDetailsResult = useProductSkuDetails({
    skuIds: productDetailsStatus === 'success' && product?.itemId ? [product.itemId] : null,
  });

  const skuDetails = skuDetailsResult.isSuccess ? skuDetailsResult.data[0] : null;
  const { acesEnabled } = useAcesFlags();

  const productAttributes = product?.productAttributes ?? [];
  const productServiceBulletin = product?.itemPsbUrls ?? [];

  const hasVisibleProductAttribute = productAttributes.some(
    (item) => item.name && item.value && !HIDDEN_SPEC_KEYS.has(item.name),
  );

  const msds =
    acesEnabled && typeof product?.itemMsdsUrl === 'object'
      ? product?.itemMsdsUrl?.[0]
      : typeof product?.itemMsdsUrl === 'string'
        ? product?.itemMsdsUrl
        : null;

  return Boolean(
    skuDetails?.warranty ||
    product?.itemWeight ||
    product?.location ||
    product?.applicationNotes?.length ||
    hasVisibleProductAttribute ||
    productServiceBulletin.length ||
    msds,
  );
};
